import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTranslation } from "react-i18next";
import { LANGS, applyLang } from "../lib/i18n";
import { OVERRIDABLE_CURRENCIES, getCurrencyOverride, setCurrencyOverride, getGeo } from "../lib/geo";
import { Home, LayoutGrid, Receipt, User, Globe, DollarSign, LogOut, LayoutDashboard, ChevronDown, Zap, LifeBuoy, KeyRound, Check } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import i18n from "../lib/i18n";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useRouterLocation();

  // Local re-render trigger when the user picks a new currency in the dropdown.
  // `useState` alone is enough; we intentionally don't lift this into context —
  // the ~5 places that read `getGeo().currency` at render time will pick up the
  // new value from localStorage on their next render.
  const [, setCurrencyTick] = useState(0);
  useEffect(() => {
    const on = () => setCurrencyTick((n) => n + 1);
    window.addEventListener("drivon:currency-changed", on);
    return () => window.removeEventListener("drivon:currency-changed", on);
  }, []);

  const dashHref = user?.role === "admin" ? "/admin" : user?.role === "supplier" ? "/supplier" : "/customer";
  const changeLang = (code) => { applyLang(code); window.location.reload(); };
  const currentLang = LANGS.find((l) => l.code === i18n.language) || LANGS[0];
  // Hide global chrome (header + bottom nav) on:
  //   - auth flows (login/register/reset/forgot/oauth callback)
  //   - public marketing landing (has its own MarketingNav + no bottom nav)
  const showChrome = !["/login", "/register", "/auth", "/auth/callback", "/forgot-password", "/reset-password", "/marketing", "/welcome"].includes(loc.pathname);
  // Immersive fullscreen booking flow — hide site header on /request.
  // Keep the bottom nav so customers can jump to Home/Activity/Account/Wallet.
  const isImmersive = loc.pathname === "/request" || loc.pathname.startsWith("/request/");

  return (
    <div className="min-h-screen flex flex-col bg-ink-bg text-white">
      {showChrome && !isImmersive && (
        <header
          className="sticky top-0 z-40 bg-ink-bg/95 backdrop-blur border-b border-ink-border"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
          data-testid="site-header"
        >
          <div className="max-w-[600px] md:max-w-[900px] mx-auto px-4 h-14 flex items-center justify-between gap-3">
            <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
              <div className="w-8 h-8 bg-white text-black rounded-full flex items-center justify-center font-black text-sm">D</div>
              <div className="text-base font-black tracking-tight">Drivons</div>
            </Link>
            <nav className="hidden md:flex items-center gap-6 text-xs font-black uppercase tracking-widest text-ink-textDim">
              <Link to="/services" className="hover:text-white" data-testid="nav-services">{t("nav.services")}</Link>
              <Link to="/estimate" className="hover:text-white" data-testid="nav-estimate">{t("nav.estimate", "Estimate")}</Link>
              <Link to="/about" className="hover:text-white" data-testid="nav-about">{t("nav.about")}</Link>
              <Link to="/pricing" className="hover:text-white" data-testid="nav-pricing">{t("nav.pricing")}</Link>
              <Link to="/contact" className="hover:text-white" data-testid="nav-contact">{t("nav.contact")}</Link>
            </nav>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 bg-ink-surface2 hover:bg-ink-surface rounded-full px-3 py-1.5 text-xs font-bold border border-ink-border" data-testid="lang-switcher">
                    <Globe size={13} /> <span>{currentLang.label}</span> <ChevronDown size={11} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" collisionPadding={8} className="bg-ink-surface border border-ink-border min-w-[160px] max-w-[calc(100vw-1rem)] text-white">
                  <DropdownMenuLabel className="text-xs text-ink-textDim">{t("language")}</DropdownMenuLabel>
                  {LANGS.map((l) => (
                    <DropdownMenuItem key={l.code} onClick={() => changeLang(l.code)}
                      className={`cursor-pointer text-white focus:bg-white focus:text-black ${l.code === currentLang.code ? "font-bold" : ""}`} data-testid={`lang-${l.code}`}>
                      {l.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Currency picker — Auto (geo-detected) unless the user overrides */}
              {(() => {
                const geo = getGeo();
                const override = getCurrencyOverride();
                const effective = (override || geo?.currency || "usd").toLowerCase();
                const label = OVERRIDABLE_CURRENCIES.find((c) => c.code === effective)?.symbol
                              || effective.toUpperCase();
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center gap-1 bg-ink-surface2 hover:bg-ink-surface rounded-full px-3 py-1.5 text-xs font-bold border border-ink-border"
                        data-testid="currency-switcher"
                        title={t("currency.title", "Currency")}
                      >
                        <DollarSign size={13} />
                        <span>{label}</span>
                        <ChevronDown size={11} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" collisionPadding={8} className="bg-ink-surface border border-ink-border min-w-[220px] max-w-[calc(100vw-1rem)] text-white max-h-[420px] overflow-y-auto">
                      <DropdownMenuLabel className="text-xs text-ink-textDim">
                        {t("currency.title", "Currency")}
                      </DropdownMenuLabel>
                      <DropdownMenuItem
                        onClick={() => setCurrencyOverride(null)}
                        className="cursor-pointer text-white focus:bg-white focus:text-black"
                        data-testid="currency-auto"
                      >
                        <div className="flex items-center justify-between w-full">
                          <span className="flex flex-col">
                            <span className={!override ? "font-bold" : ""}>
                              {t("currency.auto", "Auto")}
                              {geo?.currency && <span className="text-ink-textDim font-normal"> · {geo.currency.toUpperCase()}</span>}
                            </span>
                            <span className="text-[10px] text-ink-textDim">{t("currency.autoDesc", "Detected from your location")}</span>
                          </span>
                          {!override && <Check size={13} className="text-electric-teal" />}
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-ink-border" />
                      {OVERRIDABLE_CURRENCIES.map((c) => (
                        <DropdownMenuItem
                          key={c.code}
                          onClick={() => setCurrencyOverride(c.code)}
                          className={`cursor-pointer text-white focus:bg-white focus:text-black ${override === c.code ? "font-bold" : ""}`}
                          data-testid={`currency-${c.code}`}
                        >
                          <div className="flex items-center justify-between w-full">
                            <span>{c.label}</span>
                            {override === c.code && <Check size={13} className="text-electric-teal" />}
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })()}

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-8 h-8 rounded-full bg-ink-surface2 hover:bg-ink-surface border border-ink-border flex items-center justify-center" data-testid="user-menu">
                      {user.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" /> : <User size={14} />}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" collisionPadding={8} className="bg-ink-surface border border-ink-border min-w-[180px] max-w-[calc(100vw-1rem)] text-white">
                    <DropdownMenuLabel className="text-xs text-ink-textDim truncate">{user.email}</DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-ink-border" />
                    <DropdownMenuItem onClick={() => nav(dashHref)} className="cursor-pointer text-white focus:bg-white focus:text-black" data-testid="menu-dashboard">
                      <LayoutDashboard size={14} className="me-2" /> {t("nav.dashboard")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/support")} className="cursor-pointer text-white focus:bg-white focus:text-black" data-testid="menu-support">
                      <LifeBuoy size={14} className="me-2" /> {t("nav.support", "Help & Support")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/account/password")} className="cursor-pointer text-white focus:bg-white focus:text-black" data-testid="menu-change-password">
                      <KeyRound size={14} className="me-2" /> {t("nav.changePassword", "Change password")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} className="cursor-pointer text-red-400 focus:bg-red-500 focus:text-white" data-testid="menu-logout">
                      <LogOut size={14} className="me-2" /> {t("nav.logout")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/login" className="bg-white text-black rounded-full px-3 py-1.5 text-xs font-bold hover:bg-white/90" data-testid="nav-login">
                  {t("nav.signIn")}
                </Link>
              )}
            </div>
          </div>
        </header>
      )}

      <main className={`flex-1 ${isImmersive ? "" : "pb-nav"}`}>{children}</main>

      {showChrome && !isImmersive && <BottomNav />}
    </div>
  );
}

function BottomNav() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const loc = useRouterLocation();
  const nav = useNavigate();

  const home = "/";
  const activity = user ? (user.role === "admin" ? "/admin" : user.role === "supplier" ? "/supplier" : "/customer") : "/login";
  const services = "/services";
  const request = user?.role === "customer" ? "/request" : (user ? activity : "/login");
  const account = user ? activity : "/login";

  const isCustomer = user?.role === "customer";
  // Customers get the Uber-style layout: Home · Wallet · REQUEST · Activity · Account.
  // Non-customers keep the legacy Services browsing nav.
  const items = isCustomer
    ? [
        { key: "home", to: home, icon: Home, label: t("nav.home") },
        { key: "wallet", to: "/wallet", icon: LayoutGrid, label: t("nav.wallet", "Wallet") },
        { key: "request", to: request, icon: Zap, label: t("nav.request", "Request"), primary: true },
        { key: "activity", to: activity, icon: Receipt, label: t("nav.activity") },
        { key: "account", to: account, icon: User, label: t("nav.account") },
      ]
    : [
        { key: "home", to: home, icon: Home, label: t("nav.home") },
        { key: "services", to: services, icon: LayoutGrid, label: t("nav.services") },
        { key: "request", to: request, icon: Zap, label: t("nav.request", "Request"), primary: true },
        { key: "activity", to: activity, icon: Receipt, label: t("nav.activity") },
        { key: "account", to: account, icon: User, label: t("nav.account") },
      ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-ink-bg border-t border-ink-border" data-testid="bottom-nav">
      <div className="max-w-[600px] md:max-w-[900px] mx-auto grid grid-cols-5 h-16">
        {items.map((it) => {
          const active = loc.pathname === it.to || (it.key === "home" && loc.pathname === "/") || (it.key === "services" && loc.pathname.startsWith("/services")) || (it.key === "request" && loc.pathname.startsWith("/request")) || (it.key === "wallet" && loc.pathname.startsWith("/wallet"));
          if (it.primary) {
            return (
              <button key={it.key} onClick={() => nav(it.to)} className="flex flex-col items-center justify-center gap-1 relative" data-testid={`bnav-${it.key}`}>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center -mt-4 border-4 border-ink-bg shadow-lg ${active ? "bg-white" : "bg-brand-accent"}`}>
                  <it.icon size={20} className={active ? "text-black" : "text-white"} strokeWidth={2.8} />
                </div>
                <span className={`text-[10px] ${active ? "font-black text-white" : "font-black text-brand-accent"}`}>{it.label}</span>
              </button>
            );
          }
          return (
            <button key={it.key} onClick={() => nav(it.to)} className={`flex flex-col items-center justify-center gap-1 ${active ? "text-white" : "text-ink-textDim"}`} data-testid={`bnav-${it.key}`}>
              <it.icon size={20} strokeWidth={active ? 2.6 : 1.8} />
              <span className={`text-[10px] ${active ? "font-black" : "font-semibold"}`}>{it.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
